import { Injectable } from '@angular/core';
import { UserProfileDataService } from './user-profile-data.service';
import {
  EProfileTypeDTO,
  IDoctor,
  IPatient,
  IReceptionist,
  IRequisition,
  IRequisitionParticipant,
  RequisitionParticipantViewModel,
} from '@project/view-models';
import { TGuid } from '@core/helpers';
import { INurse } from '../view-models/nurse.view-model';

@Injectable({ providedIn: 'root' })
export class RequisitionHelpersService {
  constructor(private userProfileDataService: UserProfileDataService) {}

  getRequisitionParticipantForVideoCallConfirmation(requisition: IRequisition | null): IRequisitionParticipant | null {
    if (!requisition) return null;

    const currentUserProfile: EProfileTypeDTO | undefined = this.userProfileDataService.profile?.profileType;
    if (!currentUserProfile) return null;

    const requisitionParticipantExtractor: Partial<Record<
      EProfileTypeDTO,
      () => IDoctor | IPatient | IReceptionist | INurse | null
    >> = {
      [EProfileTypeDTO.Receptionist]: () => requisition.receptionist,
      [EProfileTypeDTO.Doctor]: () => requisition.doctors[0],
      [EProfileTypeDTO.Nurse]: () => requisition.nurse,
      [EProfileTypeDTO.Patient]: () => requisition.patient,
    };

    const requisitionProfileForParticipant = requisitionParticipantExtractor[currentUserProfile]?.();

    return requisitionProfileForParticipant
      ? RequisitionParticipantViewModel.create(requisitionProfileForParticipant)
      : null;
  }

  getRequisitionParticipantForCurrentUser(requisition: IRequisition | null): IRequisitionParticipant | null {
    if (!requisition) {
      return null;
    }

    const currentUserProfile: EProfileTypeDTO | undefined = this.userProfileDataService.profile?.profileType;
    if (!currentUserProfile) return null;

    if (!currentUserProfile) {
      return null;
    }

    const requisitionParticipantExtractor: Partial<Record<
      EProfileTypeDTO,
      () => IDoctor | IPatient | IReceptionist | INurse | null
    >> = {
      [EProfileTypeDTO.Receptionist]: () => requisition.patient,
      [EProfileTypeDTO.Doctor]: () => requisition.patient,
      [EProfileTypeDTO.Nurse]: () => requisition.patient,
      [EProfileTypeDTO.Patient]: () => requisition.doctors[0] || requisition.receptionist || requisition.nurse,
    };

    const requisitionProfileForParticipant = requisitionParticipantExtractor[currentUserProfile]?.();

    return requisitionProfileForParticipant
      ? RequisitionParticipantViewModel.create(requisitionProfileForParticipant)
      : null;
  }

  getParticipantById(requisition: IRequisition, id: TGuid): IRequisitionParticipant | undefined {
    return this.getParticipants(requisition).find((participant) => participant.id === id);
  }

  private getParticipants(requisition: IRequisition): IRequisitionParticipant[] {
    const raws: (IDoctor | IPatient | IReceptionist)[] = [
      requisition.patient,
      requisition.receptionist,
      ...requisition.doctors,
    ].filter((user) => !!user);
    return raws.map((user) => RequisitionParticipantViewModel.create(user));
  }
}
